import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAccessToken, selectUserType } from '../../../redux/selectors';
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import { getBaseUrl, getWebSocketUrl } from '../../../services/API';
import axios from 'axios';

const FrodWorkPart = () => {
  const ws = useRef(null);
  const connectionAttempted = useRef(false);
  const token = useSelector(selectAccessToken);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const utype = useSelector(selectUserType);
  const [messages, setMessages] = useState([]);
  const [sessionStatus, setSessionStatus] = useState('continue'); // 'continue', 'pause', 'end'
  const [comment, setComment] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [holdRequests, setHoldRequests] = useState([]);
  const [isHoldListVisible, setIsHoldListVisible] = useState(false);
  const [isHoldListDisabled, setIsHoldListDisabled] = useState(false);
  const [isWaitingForData, setIsWaitingForData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);

  // Добавляем CSS анимацию для вращения
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Получить проекты при монтировании
    if (token) {
      axios.get('https://cat-tools.com/antifraud-app/v1/antifraud/projects', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => {
          setProjects(res.data);
        })
        .catch(err => {
          console.error('Ошибка загрузки проектов:', err);
        });
    }
  }, [token]);

  const connectWebSocket = async () => {
    if (isConnected || isConnecting) return;

    setIsConnecting(true);

    try {
      // Сначала отправляем POST запрос
      const shiftResponse = await fetch(
        `${getBaseUrl()}/antifraud-app/v1/antifraud/shift`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            projects: selectedProjects,
            shift_type: "requests",
          }),
        }
      );

      if (!shiftResponse.ok) {
        throw new Error(`Ошибка запроса shift: ${shiftResponse.status}`);
      }

      console.log('Shift запрос выполнен успешно');

      // Затем отправляем второй POST запрос
      const workerResponse = await fetch(
        `${getBaseUrl()}/api/schedule/worker`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'start',
          }),
        }
      );

      if (!workerResponse.ok) {
        throw new Error(`Ошибка запроса worker: ${workerResponse.status}`);
      }

      console.log('Worker запрос выполнен успешно');

      // Потом подключаемся к WebSocket
    const socketUrl = `${getWebSocketUrl()}/antifraud-app-websocket/api/v1/websocket/antifraud/${token}`;
    ws.current = new WebSocket(socketUrl);

    ws.current.onopen = () => {
      console.log('WebSocket connected');
        setIsConnected(true);
        setIsConnecting(false);
        setIsWaitingForData(true); // Начинаем ожидание данных
      // Пример: можно отправить сообщение при подключении
      // ws.current.send(JSON.stringify({ type: 'ping' }));
    };

    ws.current.onmessage = event => {
      console.log('WebSocket message:', event.data);
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.type === 'data') {
            // Проверяем, если data равно null - продолжаем ожидание (показываем часы)
            if (parsedData.data === null) {
              console.log('Получена заявка с data: null, показываем компонент с часами');
              setMessages([]); // Очищаем сообщения чтобы не показывать старые заявки
              setIsWaitingForData(true); // Показываем компонент с часами
              return;
            }
            
            const newMessage = {
              id: Date.now(),
              timestamp: new Date().toLocaleTimeString(),
              data: parsedData,
            };
            // Заменяем все сообщения новым (показываем только текущую заявку)
            setMessages([newMessage]);
            setIsWaitingForData(false); // Данные получены, убираем ожидание
            setIsPaused(false); // Убираем паузу когда приходят новые данные
            setSessionStatus('continue'); // Устанавливаем зеленую кнопку активной
            setIsHoldListDisabled(false); // Разблокируем hold список при новых данных
          } else if (parsedData.type === 'status' && parsedData.data === null && parsedData.meta?.status === 'no_requests') {
            // Обрабатываем статус "нет заявок" - показываем компонент с часами
            console.log('Получен статус "нет заявок", показываем компонент с часами');
            setMessages([]); // Очищаем сообщения чтобы не показывать старые заявки
            setIsWaitingForData(true); // Показываем компонент с часами
          } else if (parsedData.type === 'status' && parsedData.data?.is_paused) {
            // Обрабатываем статус паузы
            setIsPaused(true);
            console.log('Работа приостановлена:', parsedData.message);
          } else if (parsedData.type === 'status' && parsedData.meta?.status === 'disconnected') {
            // Обрабатываем статус остановки работы
            console.log('Работа остановлена:', parsedData.message);
            handleWorkStop();
          }
        } catch (error) {
          console.error('Ошибка парсинга WebSocket сообщения:', error);
        }
    };

    ws.current.onerror = error => {
      console.error('WebSocket error:', error);
        setIsConnecting(false);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
        setIsConnected(false);
        setIsConnecting(false);
        setIsWaitingForData(false);
        connectionAttempted.current = false;
      };
    } catch (error) {
      console.error('Ошибка при подключении:', error);
      setIsConnecting(false);
      setIsWaitingForData(false);
      Notify.failure(`Ошибка подключения: ${error.message}`);
    }
  };

  const disconnectWebSocket = () => {
    if (ws.current) {
      ws.current.close();
    }
      };

  const connectToWebSocketOnly = async () => {
    console.log('connectToWebSocketOnly вызван, isConnected:', isConnected, 'isConnecting:', isConnecting, 'connectionAttempted:', connectionAttempted.current);
    
    if (isConnected || isConnecting || connectionAttempted.current) {
      console.log('Подключение отменено - уже подключен, подключается или попытка уже была');
      return;
    }

    console.log('Начинаем подключение к WebSocket');
    connectionAttempted.current = true;
    setIsConnecting(true);

    try {
      // Подключаемся только к WebSocket без POST запросов
      const socketUrl = `${getWebSocketUrl()}/antifraud-app-websocket/api/v1/websocket/antifraud/${token}`;
      ws.current = new WebSocket(socketUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected (auto)');
        setIsConnected(true);
        setIsConnecting(false);
        setIsWaitingForData(true); // Начинаем ожидание данных
      };

      ws.current.onmessage = event => {
        console.log('WebSocket message:', event.data);
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.type === 'data') {
            // Проверяем, если data равно null - продолжаем ожидание (показываем часы)
            if (parsedData.data === null) {
              console.log('Получена заявка с data: null, показываем компонент с часами');
              setMessages([]); // Очищаем сообщения чтобы не показывать старые заявки
              setIsWaitingForData(true); // Показываем компонент с часами
              return;
            }
            
            const newMessage = {
              id: Date.now(),
              timestamp: new Date().toLocaleTimeString(),
              data: parsedData,
            };
            // Заменяем все сообщения новым (показываем только текущую заявку)
            setMessages([newMessage]);
            setIsWaitingForData(false); // Данные получены, убираем ожидание
            setIsPaused(false); // Убираем паузу когда приходят новые данные
            setSessionStatus('continue'); // Устанавливаем зеленую кнопку активной
            setIsHoldListDisabled(false); // Разблокируем hold список при новых данных
          } else if (parsedData.type === 'status' && parsedData.data === null && parsedData.meta?.status === 'no_requests') {
            // Обрабатываем статус "нет заявок" - показываем компонент с часами
            console.log('Получен статус "нет заявок", показываем компонент с часами');
            setMessages([]); // Очищаем сообщения чтобы не показывать старые заявки
            setIsWaitingForData(true); // Показываем компонент с часами
          } else if (parsedData.type === 'status' && parsedData.data?.is_paused) {
            // Обрабатываем статус паузы
            setIsPaused(true);
            console.log('Работа приостановлена:', parsedData.message);
          } else if (parsedData.type === 'status' && parsedData.meta?.status === 'disconnected') {
            // Обрабатываем статус остановки работы
            console.log('Работа остановлена:', parsedData.message);
            handleWorkStop();
          }
        } catch (error) {
          console.error('Ошибка парсинга WebSocket сообщения:', error);
        }
    };

    ws.current.onerror = error => {
      console.error('WebSocket error:', error);
        setIsConnecting(false);
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
        setIsConnected(false);
        setIsConnecting(false);
        setIsWaitingForData(false);
        connectionAttempted.current = false;
      };
    } catch (error) {
      console.error('Ошибка при подключении к WebSocket:', error);
      setIsConnecting(false);
      setIsWaitingForData(false);
      connectionAttempted.current = false;
    }
  };

  // Функция для конвертации файла в base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Функция для обработки выбора файлов
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      'image/jpeg',
      'image/png', 
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const validFiles = files.filter(file => allowedTypes.includes(file.type));
    
    if (validFiles.length !== files.length) {
      Notify.warning('Некоторые файлы имеют неподдерживаемый тип. Разрешены: JPEG, PNG, PDF, TXT, DOC, DOCX');
    }

    try {
      const base64Files = await Promise.all(
        validFiles.map(async (file) => {
          const base64 = await convertFileToBase64(file);
          return {
            name: file.name,
            type: file.type,
            size: file.size,
            data: base64
          };
        })
      );
      
      setAttachedFiles(prev => [...prev, ...base64Files]);
    } catch (error) {
      console.error('Ошибка при обработке файлов:', error);
      Notify.failure('Ошибка при обработке файлов');
    }
  };

  // Функция для удаления файла
  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Функция для просмотра файла (открытие в новой вкладке)
  const viewFile = (filePath) => {
    try {
      // Используем правильный API endpoint для просмотра файлов
      const fileUrl = `${getBaseUrl()}/antifraud-app/files/${filePath}`;
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Ошибка при открытии файла:', error);
      Notify.failure('Ошибка при открытии файла');
    }
  };

  // Функция для скачивания файла
  const downloadFile = async (filePath, fileName) => {
    try {
      // Используем правильный API endpoint для скачивания файлов
      const fileUrl = `${getBaseUrl()}/antifraud-app/files/${filePath}`;
      
      // Пытаемся скачать файл через fetch с авторизацией
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Ошибка загрузки файла: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || filePath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      Notify.failure(`Ошибка при скачивании файла: ${error.message}`);
    }
  };

  // Функция для получения имени файла из пути
  const getFileName = (filePath) => {
    return filePath.split('/').pop() || 'file';
  };

  // Функция для определения типа файла и возможности просмотра
  const canPreview = (filePath) => {
    const extension = filePath.split('.').pop().toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'pdf', 'txt'].includes(extension);
  };

  // Функция для красивого форматирования времени в UTC
  const formatDateUTC = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      
      // Форматируем дату и время
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes} UTC`;
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return dateString;
    }
  };

  const sendDecision = (decision) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен');
      return;
    }

    // Проверяем, что комментарий не пустой
    if (!comment || comment.trim() === '') {
      Notify.warning('Пожалуйста, введите комментарий перед отправкой решения');
      return;
    }

    // Получаем последнее сообщение с данными
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || !lastMessage.data.data || !lastMessage.data.data.request) {
      console.error('Нет данных для отправки решения');
      return;
    }

    const requestId = lastMessage.data.data.request.id;
    
    const decisionData = {
      id: requestId,
      decision: decision,
      comment: comment || "",
    };

    // Добавляем файлы только если они есть
    if (attachedFiles.length > 0) {
      decisionData.files = attachedFiles.map(file => file.data);
    }

    const decisionMessage = {
      type: "decision",
      data: decisionData
    };

    try {
      ws.current.send(JSON.stringify(decisionMessage));
      console.log('Решение отправлено:', decisionMessage);
      
      // Очищаем комментарий и файлы после отправки
      setComment('');
      setAttachedFiles([]);
      
      // Если нажат красный квадрат (остановка), переводим на начальную страницу
      if (sessionStatus === 'end') {
        console.log('Красный квадрат нажат, переводим на начальную страницу');
        
        // Закрываем WebSocket соединение
        if (ws.current) {
          ws.current.close();
        }
        
        // Сбрасываем все состояния на начальные
        setIsConnected(false);
        setIsConnecting(false);
        setIsWaitingForData(false);
        setMessages([]);
        setIsPaused(false);
        setSessionStatus('continue');
        setComment('');
        setAttachedFiles([]);
        connectionAttempted.current = false;
        setIsHoldListVisible(false);
        setIsHoldListDisabled(false);
        setHoldRequests([]);
      }
    } catch (error) {
      console.error('Ошибка при отправке решения:', error);
    }
  };

  const handleSessionStatusChange = (newStatus) => {
    setSessionStatus(newStatus);
    
    // Отправляем сообщения в WebSocket для pause и end
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      if (newStatus === 'pause') {
        const pauseMessage = {
          type: "pause"
        };
        try {
          ws.current.send(JSON.stringify(pauseMessage));
          console.log('Pause отправлено:', pauseMessage);
        } catch (error) {
          console.error('Ошибка при отправке pause:', error);
        }
      } else if (newStatus === 'end') {
        const stopMessage = {
          type: "stop"
        };
        try {
          ws.current.send(JSON.stringify(stopMessage));
          console.log('Stop отправлено:', stopMessage);
        } catch (error) {
          console.error('Ошибка при отправке stop:', error);
        }
      }
      // Для 'continue' ничего не отправляем
    } else {
      console.error('WebSocket не подключен');
    }
  };

  const resumeWork = () => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен');
      return;
    }

    const resumeMessage = {
      type: "resume"
    };

    try {
      ws.current.send(JSON.stringify(resumeMessage));
      console.log('Resume отправлено:', resumeMessage);
      
      // При возобновлении работы устанавливаем состояние ожидания
      setIsPaused(false); // Убираем паузу
      setIsWaitingForData(true); // Показываем компонент с часами до получения данных
      setMessages([]); // Очищаем старые сообщения
      
      // Разблокируем hold список при возобновлении работы
      setIsHoldListDisabled(false);
    } catch (error) {
      console.error('Ошибка при отправке resume:', error);
    }
  };

  const handleWorkStop = async () => {
    console.log('Обрабатываем остановку работы от сервера');
    
    // Закрываем WebSocket соединение
    if (ws.current) {
      ws.current.close();
    }
    
    // Сбрасываем состояние на начальное (переходим на главную страницу)
    setIsConnected(false);
    setIsConnecting(false);
    setIsWaitingForData(false);
    setMessages([]);
    setIsPaused(false);
    setSessionStatus('continue');
    setComment('');
    connectionAttempted.current = false;
  };

  const fetchHoldRequests = async (page = 1) => {
    try {
      const response = await fetch(
        `${getBaseUrl()}/antifraud-app/v1/antifraud/hold-requests?page=${page - 1}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Ошибка запроса hold-requests: ${response.status}`);
      }

      const data = await response.json();
      console.log('Полный ответ сервера для hold requests:', data);
      
      // Проверяем разные возможные форматы ответа
      let holdRequestsData = [];
      let totalPagesData = 1;
      
      if (Array.isArray(data)) {
        // Если данные приходят как простой массив
        holdRequestsData = data;
        totalPagesData = 1;
      } else if (data?.results && Array.isArray(data.results)) {
        // Если данные приходят в формате с пагинацией
        holdRequestsData = data.results;
        totalPagesData = data.total_pages || Math.ceil((data.count || data.total || holdRequestsData.length) / 10) || 1;
      } else if (data?.data && Array.isArray(data.data)) {
        // Альтернативный формат
        holdRequestsData = data.data;
        totalPagesData = data.pages || data.total_pages || 1;
      } else {
        // Если данные в корне объекта
        holdRequestsData = data || [];
        totalPagesData = 1;
      }
      
      setHoldRequests(holdRequestsData);
      setCurrentPage(page);
      // Для тестирования принудительно устанавливаем больше 1 страницы
      const testTotalPages = Math.max(totalPagesData, 2);
      setTotalPages(testTotalPages);
      console.log('Hold requests обработаны:', holdRequestsData.length, 'записей, страница', page, 'из', testTotalPages);
    } catch (error) {
      console.error('Ошибка при получении hold requests:', error);
    }
  };

  const requestHoldItem = (requestId) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket не подключен');
      return;
    }

    const requestHoldMessage = {
      type: "request_hold",
      data: {
        request_id: requestId
      }
    };

    try {
      ws.current.send(JSON.stringify(requestHoldMessage));
      console.log('Request hold отправлено:', requestHoldMessage);
      
      // После выбора заявки скрываем список и делаем недоступным
      setIsHoldListVisible(false);
      setIsHoldListDisabled(true);
      setHoldRequests([]); // Очищаем данные
    } catch (error) {
      console.error('Ошибка при отправке request hold:', error);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      fetchHoldRequests(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchHoldRequests(currentPage + 1);
    }
  };

  // Периодическое обновление hold requests
 

    // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (ws.current) {
      ws.current.close();
      }
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          textAlign: 'center',
        }}
      >
        {!isConnected && messages.length === 0 && (
          <>
            {/* Блок проектов над кнопкой */}
            {utype === 'payment' && projects && Array.isArray(projects) && projects.length > 0 && (
              <div style={{
                width: '500px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
                alignContent: 'space-between',
                marginBottom: '18px',
                marginTop: '0',

              }}>
              
                {projects.map((proj) => {
                  const isSelected = selectedProjects.includes(proj.id || proj.name);
                  return (
                    <div
                      key={proj.id || proj.name}
                      onClick={() => {
                        const id = proj.id || proj.name;
                        setSelectedProjects((prev) =>
                          prev.includes(id)
                            ? prev.filter((item) => item !== id)
                            : [...prev, id]
                        );
                      }}
                      style={{
                        minWidth: '90px',
                        maxWidth: '120px',
                        height: '30px',
                        background: isSelected ? '#e0f2fe' : '#f9fafb',
                        border: isSelected ? '2px solid #38bdf8' : '1.2px solid #e5e7eb',
                        borderRadius: '10px',
                        padding: '6px 8px',
                        textAlign: 'center',
                        fontWeight: 500,
                        color: '#374151',
                        boxShadow: isSelected ? '0 2px 8px rgba(56,189,248,0.10)' : '0 1px 4px rgba(0,0,0,0.03)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.2s',
                        outline: isSelected ? 'none' : undefined,
                        fontSize: '13px',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {proj.name || proj.title || 'Без названия'}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={connectWebSocket}
                disabled={isConnected || isConnecting || !token}
                style={{
                  padding: '16px 32px',
                  backgroundColor: 'transparent',
                  color: isConnected ? '#4CAF50' : '#2196F3',
                  border: `2px solid ${isConnected ? '#4CAF50' : '#2196F3'}`,
                  borderRadius: '16px',
                  cursor:
                    isConnected || isConnecting || !token
                      ? 'not-allowed'
                      : 'pointer',
                  opacity: isConnected || isConnecting || !token ? 0.6 : 1,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  minWidth: '200px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {isConnecting
                  ? 'Подключение...'
                  : isConnected
                  ? 'Заявка получена'
                  : 'Получить заявку'}
              </button>
            </div>
          </>
        )}


        {!token && (
          <p style={{ color: 'red', marginTop: '10px' }}>
            Токен доступа отсутствует. Подключение невозможно.
          </p>
        )}

        {isPaused && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#856404',
              marginBottom: '20px'
            }}>
              Работа приостановлена
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#856404',
              marginBottom: '30px'
            }}>
              Вы не будете получать новые запросы
            </p>
            <button
              onClick={resumeWork}
              style={{
                padding: '16px 32px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 8px rgba(40,167,69,0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#218838';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 12px rgba(40,167,69,0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#28a745';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 8px rgba(40,167,69,0.2)';
              }}
            >
              Продолжить работу
            </button>
          </div>
        )}

        {/* Окно ожидания данных */}
        {isConnected && isWaitingForData && !isPaused && (
          <div style={{
            textAlign: 'center',
            padding: '60px 40px',
            backgroundColor: '#f0f9ff',
            border: '2px solid #0ea5e9',
            borderRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.1)'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              display: 'inline-block',
              animation: 'spin 2s linear infinite'
            }}>
              ⌛
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '12px',
              margin: '0 0 12px 0'
            }}>
              Ожидание данных
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#0369a1',
              lineHeight: '1.5',
              margin: '0'
            }}>
              Подключение к серверу установлено.<br/>
              Ожидаем поступления новых запросов для обработки...
            </p>
          </div>
        )}

                {messages.length > 0 && !isPaused && (
          <>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#374151',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              REQUEST
            </h1>
            {messages.map(message => {
            if (!message.data.data || !message.data.data.request) return null;

            const request = message.data.data.request;

            // Извлекаем только нужные данные
            const tableData = {
              Project: request.s_project?.name || 'N/A',
              'User ID': request.s_user_id || 'N/A',
              'Request ID': request.s_request_id || 'N/A',
              Sum: request.s_amount || 'N/A',
              Type: request.s_type || 'N/A',
              Date: request.s_created_at || 'N/A',
              Status: request.status || 'N/A',
            };

            // Сохраняем fundist_link для использования в ссылке
            const fundistLink = request.fundist_link;

            return (
              <table
                key={message.id}
                style={{
                  width: '100%',
                  maxWidth: '895px',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  marginBottom: '20px',
                  tableLayout: 'fixed',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                                  <thead>
                    <tr style={{ backgroundColor: 'rgb(217, 217, 217)' }}>
                    {Object.keys(tableData).map((key, index) => (
                      <th
                        key={key}
                                                 style={{
                           padding: '12px 16px',
                           fontWeight: '600',
                           color: '#374151',
                           border: 'none',
                           textAlign: 'center',
                           width: `${100 / Object.keys(tableData).length}%`,
                           fontSize: '13px',
                           letterSpacing: '0.025em',
                         }}
                      >
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: '#ffffff' }}>
                    {Object.values(tableData).map((value, index) => {
                      const key = Object.keys(tableData)[index];
                      let cellStyle = {
                        padding: '12px 16px',
                        border: 'none',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        color: '#374151',
                      };

                      // Специальные стили для разных колонок
                      if (key === 'User ID') {
                        cellStyle = {
                          ...cellStyle,
                          backgroundColor: '#bfdbfe',
                          fontWeight: '600',
                          color: '#1e40af',
                        };
                      } else if (key === 'Status') {
                        cellStyle = {
                          ...cellStyle,
                          color: value === 'inprogress' ? '#059669' : '#374151',
                          fontWeight: '500',
                        };
                      } else if (key === 'Sum') {
                        cellStyle = {
                          ...cellStyle,
                          fontWeight: '600',
                          color: parseFloat(value) < 0 ? '#dc2626' : '#059669',
                        };
                      }

                      return (
                        <td key={index} style={cellStyle}>
                          {key === 'User ID' && fundistLink ? (
                            <a
                              href={fundistLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#1e40af',
                                textDecoration: 'none',
                                fontWeight: '600',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={e => {
                                e.target.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={e => {
                                e.target.style.textDecoration = 'none';
                              }}
                            >
                              {String(value)}
                            </a>
                          ) : key === 'Date' ? (
                            formatDateUTC(value)
                          ) : (
                            String(value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
                              </table>
              );
            })}

            {/* Отображение комментариев с веб-сокетов */}
            {messages.length > 0 && messages[0]?.data?.data?.request?.comments && messages[0].data.data.request.comments.length > 0 && (
              <div style={{
                marginTop: '15px',
                marginBottom: '15px',
                textAlign: 'left',
                maxWidth: '895px',
                margin: '15px auto 15px auto'
              }}>
                <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '10px',
                  maxHeight: '135px',
                  overflowY: 'auto'
                }}>
                  {messages[0].data.data.request.comments.map((comment) => (
                    <div key={comment.id} style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      marginBottom: '3px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            fontWeight: '600',
                            color: '#1e40af',
                            fontSize: '12px'
                          }}>
                            {comment.user_display || comment.username}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '10px',
                          color: '#6b7280'
                        }}>
                          {formatDateUTC(comment.created_at)}
                        </span>
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#374151',
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {comment.text}
                      </div>
                      {comment.files && Array.isArray(comment.files) && comment.files.length > 0 && (
                        <div style={{
                          marginTop: '4px',
                          fontSize: '10px',
                          color: '#6b7280'
                        }}>
                          {Array.isArray(comment.files) ? (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}>
                              <span style={{ marginBottom: '2px' }}>Файлы:</span>
                              {comment.files.map((filePath, fileIndex) => (
                                <div key={fileIndex} style={{
                                  display: 'flex',
                                  gap: '4px',
                                  alignItems: 'center'
                                }}>
                                  <span style={{
                                    fontSize: '10px',
                                    color: '#374151',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {getFileName(filePath)}
                                  </span>
                                  <button
                                    onClick={() => downloadFile(filePath, getFileName(filePath))}
                                    style={{
                                      background: 'none',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '3px',
                                      padding: '1px 4px',
                                      fontSize: '9px',
                                      color: '#2563eb',
                                      cursor: 'pointer',
                                      textDecoration: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#eff6ff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    📥 Скачать
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{
                              display: 'flex',
                              gap: '4px',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                fontSize: '10px',
                                color: '#374151',
                                maxWidth: '150px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {getFileName(comment.files)}
                              </span>
                              <button
                                onClick={() => downloadFile(comment.files, getFileName(comment.files))}
                                style={{
                                  background: 'none',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '3px',
                                  padding: '1px 4px',
                                  fontSize: '9px',
                                  color: '#2563eb',
                                  cursor: 'pointer',
                                  textDecoration: 'none'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#eff6ff';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                📥 Скачать
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{
              marginTop: '30px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Введите комментарий..."
                style={{
                  width: '600px',
                  height: '150px',
                  padding: '12px',
                  border: '2px solid rgb(217, 217, 217)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2196F3';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgb(217, 217, 217)';
                }}
              />
              
              {/* Блок для прикрепления файлов */}
              <div style={{
                marginTop: '15px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px'
              }}>
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.txt,.doc,.docx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    border: '2px solid rgb(217, 217, 217)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#2196F3';
                    e.target.style.color = '#2196F3';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = 'rgb(217, 217, 217)';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  📎 Прикрепить файлы
                </label>
                
                {/* Отображение прикрепленных файлов */}
                {attachedFiles.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    maxWidth: '600px',
                    width: '100%'
                  }}>
                    {attachedFiles.map((file, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          minWidth: 0
                        }}>
                          <span style={{
                            color: '#374151',
                            fontWeight: '500',
                            truncate: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}>
                            {file.name}
                          </span>
                          <span style={{
                            color: '#6b7280',
                            fontSize: '11px'
                          }}>
                            ({Math.round(file.size / 1024)} KB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '4px',
                            fontSize: '14px',
                            lineHeight: '1'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#fee2e2';
                            e.target.style.borderRadius = '4px';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px',
              marginTop: '30px',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={() => sendDecision('approve')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: 'rgb(30, 175, 105)',
                  border: '2px solid rgb(217, 217, 217)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                Approve
              </button>
              {/* Остальные кнопки */}
              <button 
                onClick={() => sendDecision('decline')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: 'rgb(255, 84, 84)',
                  border: '2px solid rgb(217, 217, 217)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                Decline
              </button>
              {utype !== "tl" && (
                <button 
                  onClick={() => sendDecision('escalade')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'transparent',
                    color: 'rgb(30, 140, 175)',
                    border: '2px solid rgb(217, 217, 217)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  Escalade
                </button>
              )}
              <button 
                onClick={() => sendDecision('hold')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: 'rgb(175, 117, 30)',
                  border: '2px solid rgb(217, 217, 217)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                Hold
              </button>
            </div>
            
            {/* Hold Requests List */}
            {isConnected && !isHoldListDisabled && (
              <div style={{
                marginTop: '40px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0'
                  }}>
                    Hold Requests{holdRequests.length > 0 ? ` (${holdRequests.length})` : ''}
                  </h3>
                  <button
                    onClick={() => {
                      const newVisibility = !isHoldListVisible;
                      setIsHoldListVisible(newVisibility);
                      // Загружаем данные каждый раз при открытии
                      if (newVisibility) {
                        fetchHoldRequests();
                      }
                    }}
                    disabled={isHoldListDisabled}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      color: '#374151',
                      border: '1px solid rgb(217, 217, 217)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: isHoldListDisabled ? 'not-allowed' : 'pointer',
                      opacity: isHoldListDisabled ? 0.5 : 1,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isHoldListDisabled) {
                        e.target.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isHoldListDisabled) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {isHoldListVisible ? '▼ Скрыть' : '▶ Показать'}
                  </button>
                </div>

                                {isHoldListVisible && (
                  <div>
                    <table
                      style={{
                        width: '100%',
                        maxWidth: '895px',
                        borderCollapse: 'collapse',
                        fontSize: '14px',
                        margin: '0 auto',
                        tableLayout: 'fixed',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                    <thead>
                      <tr style={{ backgroundColor: 'rgb(217, 217, 217)' }}>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Project
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          User ID
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Request ID
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Sum
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Type
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Date
                        </th>
                        <th style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#374151',
                          border: 'none',
                          textAlign: 'center',
                          width: '14.3%',
                          fontSize: '13px',
                          letterSpacing: '0.025em',
                        }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdRequests.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{
                            padding: '40px 16px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px',
                            fontStyle: 'italic'
                          }}>
                            Нет доступных Hold requests
                          </td>
                        </tr>
                      ) : (
                        holdRequests.map((request, index) => (
                        <tr 
                          key={request.id || index} 
                          onClick={() => requestHoldItem(request.id)}
                          style={{ 
                            backgroundColor: '#ffffff',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }}
                        >
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            color: '#374151',
                            textAlign: 'center'
                          }}>
                            {request.s_project?.name || 'N/A'}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            backgroundColor: '#bfdbfe',
                            fontWeight: '600',
                            color: '#1e40af',
                            textAlign: 'center'
                          }}>
                            {request.fundist_link ? (
                              <a
                                href={request.fundist_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#1e40af',
                                  textDecoration: 'none',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={e => {
                                  e.target.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={e => {
                                  e.target.style.textDecoration = 'none';
                                }}
                              >
                                {request.s_user_id || 'N/A'}
                              </a>
                            ) : (
                              request.s_user_id || 'N/A'
                            )}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            color: '#374151',
                            textAlign: 'center'
                          }}>
                            {request.s_request_id || 'N/A'}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            fontWeight: '600',
                            color: parseFloat(request.s_amount) < 0 ? '#dc2626' : '#059669',
                            textAlign: 'center'
                          }}>
                            {request.s_amount || 'N/A'} {request.s_currency || ''}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            color: '#374151',
                            textAlign: 'center'
                          }}>
                            {request.s_type || 'N/A'}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            color: '#374151',
                            textAlign: 'center'
                          }}>
                            {formatDateUTC(request.s_created_at) || 'N/A'}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            border: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            fontWeight: '600',
                            color: '#fbbf24',
                            textAlign: 'center'
                          }}>
                            HOLD
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  
                  {/* Пагинация - показываем всегда для отладки */}
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    Отладка: страница {currentPage} из {totalPages}, элементов: {holdRequests.length}
                  </div>
                  
                  {/* Пагинация */}
                  {(
                    <div style={{
                      marginTop: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '15px'
                    }}>
                      <button
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: currentPage === 1 ? '1px solid #e0e0e0' : '1px solid #374151',
                          backgroundColor: 'transparent',
                          color: currentPage === 1 ? '#e0e0e0' : '#374151',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          outline: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage > 1) {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage > 1) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        ←
                      </button>
                      
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {currentPage}
                      </span>
                      
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: currentPage === totalPages ? '1px solid #e0e0e0' : '1px solid #374151',
                          backgroundColor: 'transparent',
                          color: currentPage === totalPages ? '#e0e0e0' : '#374151',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          outline: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage < totalPages) {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage < totalPages) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        →
                      </button>
                    </div>
                  )}
                  </div>
                )}
              </div>
            )}


          </>
        )}

        {/* Кнопки управления сессией - отображаются при подключении */}
        {isConnected && !isPaused && (
          <div style={{
            marginTop: '30px',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-flex',
              gap: '8px'
            }}>
              <button
                onClick={() => handleSessionStatusChange('pause')}
                disabled={sessionStatus === 'pause' || sessionStatus === 'end'}
                style={{
                  width: '44px',
                  height: '44px',
                  border: sessionStatus === 'pause' ? '2px solid #FF9800' : '2px solid #e0e0e0',
                  borderRadius: '50%',
                  fontSize: '16px',
                  cursor: (sessionStatus === 'pause' || sessionStatus === 'end') ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'transparent',
                  color: sessionStatus === 'pause' ? '#FF9800' : '#999',
                  opacity: (sessionStatus === 'pause' || sessionStatus === 'end') ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '0',
                  textAlign: 'center',
                  padding: '0',
                  margin: '0',
                  outline: 'none',
                  verticalAlign: 'middle'
                }}
              >
                <span style={{ 
                  display: 'block',
                  lineHeight: '1'
                }}>⏸</span>
              </button>
              
              <button
                onClick={() => handleSessionStatusChange('end')}
                disabled={sessionStatus === 'pause' || sessionStatus === 'end'}
                style={{
                  width: '44px',
                  height: '44px',
                  border: sessionStatus === 'end' ? '2px solid #f44336' : '2px solid #e0e0e0',
                  borderRadius: '50%',
                  fontSize: '16px',
                  cursor: (sessionStatus === 'pause' || sessionStatus === 'end') ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'transparent',
                  color: sessionStatus === 'end' ? '#f44336' : '#999',
                  opacity: (sessionStatus === 'pause' || sessionStatus === 'end') ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '0',
                  textAlign: 'center',
                  padding: '0',
                  margin: '0',
                  outline: 'none',
                  verticalAlign: 'middle'
                }}
              >
                <span style={{ 
                  display: 'block',
                  lineHeight: '1'
                }}>⏹</span>
              </button>
            </div>
            
            {/* Текст под кнопкой паузы */}
            {sessionStatus === 'pause' && (
              <div style={{
                marginTop: '10px',
                fontSize: '14px',
                color: '#FF9800',
                fontWeight: '500'
              }}>
                Завершите текущую заявку
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    );
};

export default FrodWorkPart;
